import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Input } from "./Input";
import { Button } from "./Button";

export type RigSelection = {
  canonical_id: string | null;
  user_rig_id: number | null;
  snapshot: string;
};

function labelFor(r: any) {
  return r.canonical_name ?? r.display_name ?? "";
}

export function RigPicker({
  id,
  label,
  value,
  onChange,
  speciesFilter,
  placeholder,
}: {
  id: string;
  label: string;
  value: RigSelection;
  onChange: (v: RigSelection) => void;
  speciesFilter?: string;
  placeholder?: string;
}) {
  const queryClient = useQueryClient();
  const [query, setQuery] = useState(value.snapshot || "");
  const [open, setOpen] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState("");
  const [category, setCategory] = useState("");
  const [privateOnly, setPrivateOnly] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitMsg, setSubmitMsg] = useState("");

  useEffect(() => setQuery(value.snapshot || ""), [value.snapshot]);

  const params = new URLSearchParams({ q: query.trim() });
  if (speciesFilter) params.set("species", speciesFilter);
  const searchEnabled = query.trim().length >= 2;

  const { data: results = [] } = useQuery({
    queryKey: ["rig_search", query, speciesFilter],
    queryFn: () => fetch(`/app-api/rigs/search?${params}`).then((r) => (r.ok ? r.json() : [])),
    enabled: searchEnabled && open,
    staleTime: 30_000,
  });
  const { data: userRigs = [] } = useQuery({
    queryKey: ["carpintel_user_rigs"],
    queryFn: () => fetch("/app-api/rigs/mine").then((r) => (r.ok ? r.json() : [])),
  });

  const matchedUserRigs = (userRigs as any[]).filter((r: any) =>
    !query || String(r.display_name ?? "").toLowerCase().includes(query.toLowerCase()),
  );

  function select(r: any) {
    const snapshot = labelFor(r);
    onChange({
      canonical_id: r.linked_canonical_rig_id ?? (r.id?.startsWith?.("rig_") ? r.id : null),
      user_rig_id: r.user_rig_id ?? (r.id && !r.id?.startsWith?.("rig_") ? Number(r.id) : null),
      snapshot,
    });
    setQuery(snapshot);
    setOpen(false);
    setSubmitMsg("");
  }

  async function addAndUse() {
    const name = newName.trim();
    if (!name || submitting) return;
    setSubmitting(true);
    setSubmitMsg("");
    try {
      const response = await fetch("/app-api/rigs/add", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          presentation_category: category.trim() || null,
          species_applicability: speciesFilter || "both",
          private_only: privateOnly,
        }),
      });
      const data = await response.json();
      if (!response.ok || !data.id) throw new Error(data.error || "Could not add rig");
      onChange({ canonical_id: null, user_rig_id: Number(data.id), snapshot: data.display_name || name });
      setQuery(data.display_name || name);
      setShowAdd(false);
      setNewName("");
      setCategory("");
      setOpen(false);
      await queryClient.invalidateQueries({ queryKey: ["carpintel_user_rigs"] });
      setSubmitMsg(privateOnly ? "Private rig added and selected." : "Added and selected. Admin will review it for the shared rig list.");
    } catch (error) {
      setSubmitMsg(error instanceof Error ? error.message : "Could not add rig");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="relative">
      <label htmlFor={id} className="mb-1 block text-sm font-medium text-primary">{label}</label>
      <div className="flex gap-2">
        <Input
          id={id}
          value={query}
          onChange={(event) => {
            const next = event.target.value;
            setQuery(next);
            onChange({ canonical_id: null, user_rig_id: null, snapshot: next });
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => window.setTimeout(() => setOpen(false), 180)}
          placeholder={placeholder ?? "Spinner, blowback, multi rig…"}
          autoComplete="off"
        />
        {value.snapshot ? (
          <button type="button" className="shrink-0 px-2 text-xs text-secondary" aria-label="Clear rig" onClick={() => { setQuery(""); onChange({ canonical_id: null, user_rig_id: null, snapshot: "" }); }}>✕</button>
        ) : null}
      </div>

      {open && (searchEnabled || matchedUserRigs.length) ? (
        <div className="absolute left-0 right-0 z-50 mt-1 max-h-72 overflow-y-auto rounded-lg border border-border bg-raised shadow-xl">
          {(results as any[]).map((row: any) => (
            <button key={row.id} type="button" onMouseDown={() => select(row)} className="block w-full px-3 py-2.5 text-left hover:bg-inset">
              <span className="block text-sm font-medium text-primary">{labelFor(row)}</span>
              <span className="text-xs text-secondary">{row.bait_presentation || "Known rig"}</span>
            </button>
          ))}
          {matchedUserRigs.length ? <p className="border-t border-border px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-secondary">Your added rigs</p> : null}
          {matchedUserRigs.map((row: any) => (
            <button key={`user-${row.id}`} type="button" onMouseDown={() => select({ ...row, user_rig_id: Number(row.id), id: null })} className="block w-full px-3 py-2.5 text-left hover:bg-inset">
              <span className="block text-sm font-medium text-primary">{row.display_name}</span>
              <span className="text-xs text-secondary">{row.linked_canonical_rig_id ? "Reviewed · linked to shared rig" : "Personal rig"}</span>
            </button>
          ))}
          <button
            type="button"
            onMouseDown={() => { setNewName(query.trim()); setShowAdd(true); setOpen(false); setSubmitMsg(""); }}
            className="w-full border-t border-border px-3 py-2.5 text-left text-xs font-semibold text-accent"
          >
            + Add rig not listed
          </button>
        </div>
      ) : null}

      {showAdd ? (
        <div className="mt-2 space-y-3 rounded-lg border border-accent/40 bg-raised p-3">
          <p className="text-sm font-semibold text-primary">Add a missing rig</p>
          <p className="text-xs leading-5 text-secondary">Use it immediately. Unless you keep it private, Admin can approve it or link it to an existing duplicate later.</p>
          <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Rig name" />
          <Input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="Presentation, e.g. pop-up (optional)" />
          <label className="flex items-center gap-2 text-xs text-secondary"><input type="checkbox" checked={privateOnly} onChange={(e) => setPrivateOnly(e.target.checked)} /> Keep this rig private</label>
          <div className="flex gap-2">
            <Button type="button" size="sm" onClick={addAndUse} isLoading={submitting}>Add and use now</Button>
            <Button type="button" size="sm" variant="tertiary" onClick={() => setShowAdd(false)}>Cancel</Button>
          </div>
        </div>
      ) : null}
      {submitMsg ? <p className="mt-1 text-xs text-secondary" role="status">{submitMsg}</p> : null}
    </div>
  );
}
